/**
 * The org chart over the dated reporting line (piece 10; decision 0079). 🔨 A
 * reporting line is an `EmploymentPosition` row whose `bossEmploymentId` points
 * at an `EmploymentRecord`, never at a Position and never at a Person, so every
 * function here walks employments. 🔨 The chart draws organization employments
 * alone: a customer contact has no place in it.
 *
 * 🔨 The cycle guard is the one shared transitive resolver, never a second tree
 * walk: `moveWouldCycle` asks the resolver whether the proposed manager already
 * reports to the person being moved.
 */
import { TransitiveCycleError, transitiveChain } from './transitive';
import type { TreeNode } from '../composites/treeList';

/** One employment's position in force, as the chart reads it. */
export interface OrgPositionRow {
  employmentId: string;
  personId: string;
  personName: string;
  title: string | null;
  departmentId: string | null;
  departmentName: string | null;
  bossEmploymentId: string | null;
  bossSource: string | null;
  /** 🔒 The worker's level on this position, and its name (T441; decision 1123); absent from an older reader. */
  skillLevelId?: string | null;
  skillLevelName?: string | null;
}

/** A node of the Nested Tree List, carrying the facts the org chart draws on a row. */
export interface OrgChartNode extends TreeNode {
  employmentId: string;
  personId: string;
  title: string | null;
  departmentId: string | null;
  departmentName: string | null;
  bossEmploymentId: string | null;
  bossSource: string | null;
  /** 🔒 The level the position holds, which the card reads after the title and the Move window opens on (T441; decision 1123). */
  skillLevelId: string | null;
  skillLevelName: string | null;
  /** How many employments report to this one, directly. */
  directReports: number;
  /** True where the chain above this row never reaches the root employment. */
  broken: boolean;
}

export interface OrgChart {
  rootEmploymentId: string | null;
  /** The placed rows, parented by their manager, for the Nested Tree List. */
  nodes: OrgChartNode[];
  /** 🔨 A person under nobody stands in its own group above the tree, never silently at the root. */
  unplaced: OrgChartNode[];
}

/** 🔨 The refusal a move that would loop the chart carries, in the plan's own words. */
export const ORG_MOVE_CYCLE_REFUSAL = 'a person cannot report to their own report, so this move is refused';

/** The manager of an employment, as the rows in force say. */
function bossLookup(rows: readonly OrgPositionRow[]): (id: string) => string | null {
  const boss = new Map(rows.map((row) => [row.employmentId, row.bossEmploymentId ?? null]));
  return (id: string) => boss.get(id) ?? null;
}

/** Direct reports of one employment, walked down the dated `bossEmploymentId` (piece 12). */
export function directReportsOf(rows: readonly OrgPositionRow[], employmentId: string): OrgPositionRow[] {
  return rows.filter((row) => row.bossEmploymentId === employmentId && row.employmentId !== employmentId);
}

/**
 * Whether the chain above an employment reaches the root. A row whose manager
 * has no row of their own, or whose chain loops, does not reach it, and the
 * chart lists the row as unplaced rather than drawing a tree with no top.
 */
export function reachesRoot(rows: readonly OrgPositionRow[], employmentId: string, rootEmploymentId: string | null): boolean {
  return reachesRootLookup(rows, rootEmploymentId)(employmentId);
}

/**
 * The same answer for every row, walked once. 💰 Asking it per row walked the
 * chain per row, so the chart and the exceptions count were quadratic in the
 * number of employments, and the count runs for every viewer on every landing
 * page (found in review, 2026-09-15). Each answer is remembered for the whole
 * chain that produced it, so the walk is linear.
 */
export function reachesRootLookup(rows: readonly OrgPositionRow[], rootEmploymentId: string | null): (employmentId: string) => boolean {
  const boss = bossLookup(rows);
  const answered = new Map<string, boolean>();
  if (rootEmploymentId !== null) answered.set(rootEmploymentId, true);
  return (employmentId: string): boolean => {
    if (rootEmploymentId === null) return false;
    const path: string[] = [];
    const walking = new Set<string>();
    let at: string | null = employmentId;
    let answer = false;
    while (at !== null) {
      const held = answered.get(at);
      if (held !== undefined) {
        answer = held;
        break;
      }
      // A chain that meets itself reaches nothing, which is the answer the resolver's cycle guard gives.
      if (walking.has(at)) break;
      walking.add(at);
      path.push(at);
      at = boss(at);
    }
    for (const id of path) answered.set(id, answer);
    return answer;
  };
}

/**
 * The chart as at a date: the rows in force, parented by the manager each names,
 * with the root at the top. A row that never reaches the root lands in
 * `unplaced` with its parent dropped, so the tree the page draws has exactly one
 * top and the broken lines are visible rather than hidden under it.
 */
export function buildOrgChart(rows: readonly OrgPositionRow[], rootEmploymentId: string | null): OrgChart {
  const known = new Set(rows.map((row) => row.employmentId));
  const placedAt = reachesRootLookup(rows, rootEmploymentId);
  const reports = new Map<string, number>();
  for (const row of rows) {
    if (!row.bossEmploymentId || row.bossEmploymentId === row.employmentId) continue;
    reports.set(row.bossEmploymentId, (reports.get(row.bossEmploymentId) ?? 0) + 1);
  }
  const nodes: OrgChartNode[] = [];
  const unplaced: OrgChartNode[] = [];
  for (const row of rows) {
    const placed = placedAt(row.employmentId);
    const parentId = row.employmentId === rootEmploymentId || !placed ? null : row.bossEmploymentId;
    const node: OrgChartNode = {
      id: row.employmentId,
      parentId: parentId && known.has(parentId) ? parentId : null,
      label: row.personName,
      employmentId: row.employmentId,
      personId: row.personId,
      title: row.title,
      departmentId: row.departmentId,
      departmentName: row.departmentName,
      bossEmploymentId: row.bossEmploymentId,
      bossSource: row.bossSource,
      skillLevelId: row.skillLevelId ?? null,
      skillLevelName: row.skillLevelName ?? null,
      directReports: reports.get(row.employmentId) ?? 0,
      broken: !placed,
    };
    node.count = node.directReports;
    if (placed) nodes.push(node);
    else unplaced.push(node);
  }
  return { rootEmploymentId, nodes, unplaced };
}

/**
 * One drawn card and what hangs under it (decision 0463). A report who manages
 * nobody is a leaf and stacks; a report who manages somebody is a branch and
 * takes a column of its own.
 */
export interface OrgBranch {
  node: OrgChartNode;
  /** Reports who manage somebody themselves, each drawn as its own column below. */
  branches: OrgBranch[];
  /** 🔨 Reports who manage nobody stack in one column instead of widening the chart. */
  leaves: OrgChartNode[];
  /** Everybody below this card, however deep, counted before any branch is folded. */
  below: number;
  /** True where this card is folded, so nothing under it is drawn. */
  folded: boolean;
}

/**
 * The chart as it is drawn (decision 0463): the root at the top, each manager a
 * column below their own manager, and every report who manages nobody stacked
 * into one column rather than spread sideways. 🔨 The count a folded card shows
 * is taken before folding, so it names everybody it hides.
 *
 * Siblings are ordered by name, so the same chart draws the same way twice.
 */
export function condensedOrgChart(nodes: readonly OrgChartNode[], folded: ReadonlySet<string> = new Set()): OrgBranch[] {
  const byParent = new Map<string | null, OrgChartNode[]>();
  for (const node of nodes) {
    const key = node.parentId ?? null;
    const group = byParent.get(key);
    if (group) group.push(node);
    else byParent.set(key, [node]);
  }
  for (const group of byParent.values()) group.sort((a, b) => a.label.localeCompare(b.label));
  // A chart that already loops is drawn to where it meets itself, never walked forever.
  const build = (node: OrgChartNode, above: ReadonlySet<string>): OrgBranch => {
    const walked = new Set([...above, node.id]);
    const children = (byParent.get(node.id) ?? []).filter((child) => !walked.has(child.id));
    const under = children.map((child) => build(child, walked));
    const isFolded = folded.has(node.id);
    return {
      node,
      branches: isFolded ? [] : under.filter((branch) => branch.below > 0),
      leaves: isFolded ? [] : under.filter((branch) => branch.below === 0).map((branch) => branch.node),
      below: under.reduce((count, branch) => count + 1 + branch.below, 0),
      folded: isFolded,
    };
  };
  return (byParent.get(null) ?? []).map((root) => build(root, new Set()));
}

/**
 * 🔨 Cycles are refused at save. The proposed manager is a cycle when it is the
 * person themselves, or when the chain above the proposed manager already passes
 * through them — a person cannot report to their own report.
 */
export function moveWouldCycle(rows: readonly OrgPositionRow[], employmentId: string, newBossEmploymentId: string | null): boolean {
  if (!newBossEmploymentId) return false;
  if (newBossEmploymentId === employmentId) return true;
  try {
    return transitiveChain(newBossEmploymentId, bossLookup(rows), { label: 'management chain' }).includes(employmentId);
  } catch (error) {
    if (error instanceof TransitiveCycleError) return true;
    throw error;
  }
}

/**
 * The day the position in force is ended when a move takes effect. 🔨 The old
 * row ends the day before the new one opens, so no date is covered by two
 * positions and a chain read as at any day answers once.
 */
export function dayBefore(date: Date): Date {
  return new Date(date.getTime() - 24 * 60 * 60 * 1000);
}

/** The three shapes a reporting-line exception takes, each resolved by a move. */
export type OrgExceptionKind = 'Inferred' | 'NoManager' | 'ChainBreak';

export interface OrgExceptionRow {
  employmentId: string;
  personId: string;
  personName: string;
  title: string | null;
  departmentName: string | null;
  kind: OrgExceptionKind;
  /** What broke, in a sentence the page shows beside the person. */
  what: string;
  /** The open `ChecklistItem` the walk raised, where one stands behind the row. */
  checklistItemId: string | null;
}

/**
 * The page's own walk: a row flagged Inferred is a re-pointed line awaiting
 * confirmation, a row with no manager that is not the root has nobody above it,
 * and a row whose chain stops short of the root is a break further up.
 */
export function orgExceptions(rows: readonly OrgPositionRow[], rootEmploymentId: string | null): OrgExceptionRow[] {
  const out: OrgExceptionRow[] = [];
  const placedAt = reachesRootLookup(rows, rootEmploymentId);
  for (const row of rows) {
    if (row.employmentId === rootEmploymentId) continue;
    const base = { employmentId: row.employmentId, personId: row.personId, personName: row.personName, title: row.title, departmentName: row.departmentName, checklistItemId: null };
    if (!row.bossEmploymentId) {
      out.push({ ...base, kind: 'NoManager', what: 'reports to nobody, and the approval chain above them runs nowhere' });
      continue;
    }
    if (!placedAt(row.employmentId)) {
      out.push({ ...base, kind: 'ChainBreak', what: 'the chain above this person stops before the root employment' });
      continue;
    }
    if (row.bossSource === 'Inferred') {
      out.push({ ...base, kind: 'Inferred', what: 'a departure re-pointed this line one level up, and nobody has confirmed it' });
    }
  }
  return out;
}
